import React, { useState } from 'react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { API_BASE } from '../lib/api';
import { CONTACT } from '../lib/contactInfo';
import { WhatsAppIcon } from './icons/WhatsAppIcon';
import { InstagramIcon } from './icons/InstagramIcon';

const INCOTERMS_OPTIONS = ['FOB', 'CIF', 'EXW'];
const INCOTERMS_LABELS = { FOB: 'Free on Board', CIF: 'Cost, Insurance & Freight', EXW: 'Ex Works' };

function ContactItem({ icon, label, href, display, external = false }) {
  return (
    <div className="flex gap-4 items-center">
      <div className="w-12 h-12 bg-brand-green flex items-center justify-center flex-shrink-0 text-white">
        {icon}
      </div>
      <div>
        <p className="text-sm font-semibold text-slate-900">{label}</p>
        <a
          href={href}
          {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
          className="text-sm text-brand-orange hover:underline"
        >
          {display}
        </a>
      </div>
    </div>
  );
}

export default function QuoteForm() {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    full_name: '',
    company_name: '',
    email: '',
    whatsapp: '',
    country: '',
    product_interest: '',
    estimated_quantity: '',
    incoterms: '',
    message: '',
  });
  const [status, setStatus] = useState(null); // 'success' | 'error' | null
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      await axios.post(`${API_BASE}/quotes`, form);
      setStatus('success');
      setForm({
        full_name: '', company_name: '', email: '', whatsapp: '',
        country: '', product_interest: '', estimated_quantity: '', incoterms: '', message: '',
      });
    } catch {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const productOptions = t('quote.productOptions');

  return (
    <section id="quote" className="py-24 md:py-32 bg-white" data-testid="quote-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <p className="text-sm font-semibold text-brand-orange uppercase tracking-[0.2em] mb-4">
              {t('quote.sectionLabel')}
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-6">
              {t('quote.title')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-10">
              {t('quote.subtitle')}
            </p>

            <div className="space-y-6">
              <ContactItem
                icon={<Send className="w-5 h-5" />}
                label="Email"
                href={`mailto:${CONTACT.email}`}
                display={CONTACT.email}
              />
              <ContactItem
                icon={<WhatsAppIcon />}
                label="WhatsApp"
                href={CONTACT.whatsapp.href}
                display={CONTACT.whatsapp.display}
                external
              />
              <ContactItem
                icon={<InstagramIcon />}
                label="Instagram"
                href={CONTACT.instagram.href}
                display={CONTACT.instagram.handle}
                external
              />
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-100 p-8 md:p-10">
            {status === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 flex items-start gap-3" data-testid="quote-success-message">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-800">{t('quote.success')}</p>
              </div>
            )}
            {status === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 flex items-start gap-3" data-testid="quote-error-message">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{t('quote.error')}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="quote-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="full_name" className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2 block">
                    {t('quote.fullName')} *
                  </Label>
                  <Input
                    id="full_name"
                    required
                    value={form.full_name}
                    onChange={(e) => handleChange('full_name', e.target.value)}
                    className="rounded-none border-slate-200 focus:border-brand-green focus:ring-brand-green"
                    data-testid="input-full-name"
                  />
                </div>
                <div>
                  <Label htmlFor="company_name" className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2 block">
                    {t('quote.companyName')} *
                  </Label>
                  <Input
                    id="company_name"
                    required
                    value={form.company_name}
                    onChange={(e) => handleChange('company_name', e.target.value)}
                    className="rounded-none border-slate-200 focus:border-brand-green focus:ring-brand-green"
                    data-testid="input-company-name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2 block">
                    {t('quote.email')} *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="rounded-none border-slate-200 focus:border-brand-green focus:ring-brand-green"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label htmlFor="whatsapp" className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2 block">
                    {t('quote.whatsapp')} *
                  </Label>
                  <Input
                    id="whatsapp"
                    required
                    value={form.whatsapp}
                    onChange={(e) => handleChange('whatsapp', e.target.value)}
                    className="rounded-none border-slate-200 focus:border-brand-green focus:ring-brand-green"
                    data-testid="input-whatsapp"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="country" className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2 block">
                  {t('quote.country')} *
                </Label>
                <Input
                  id="country"
                  required
                  value={form.country}
                  onChange={(e) => handleChange('country', e.target.value)}
                  className="rounded-none border-slate-200 focus:border-brand-green focus:ring-brand-green"
                  data-testid="input-country"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2 block">
                    {t('quote.productInterest')} *
                  </Label>
                  <Select
                    value={form.product_interest}
                    onValueChange={(val) => handleChange('product_interest', val)}
                    required
                  >
                    <SelectTrigger className="rounded-none border-slate-200" data-testid="select-product">
                      <SelectValue placeholder={t('quote.selectProduct')} />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.isArray(productOptions) && productOptions.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="estimated_quantity" className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2 block">
                    {t('quote.estimatedQuantity')} *
                  </Label>
                  <Input
                    id="estimated_quantity"
                    required
                    placeholder="e.g., 500 pcs or 2,000 kg"
                    value={form.estimated_quantity}
                    onChange={(e) => handleChange('estimated_quantity', e.target.value)}
                    className="rounded-none border-slate-200 focus:border-brand-green focus:ring-brand-green"
                    data-testid="input-quantity"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2 block">
                  {t('quote.incoterms')} *
                </Label>
                <Select
                  value={form.incoterms}
                  onValueChange={(val) => handleChange('incoterms', val)}
                  required
                >
                  <SelectTrigger className="rounded-none border-slate-200" data-testid="select-incoterms">
                    <SelectValue placeholder={t('quote.selectIncoterms')} />
                  </SelectTrigger>
                  <SelectContent>
                    {INCOTERMS_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt} - {INCOTERMS_LABELS[opt]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="message" className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2 block">
                  {t('quote.message')}
                </Label>
                <Textarea
                  id="message"
                  rows={4}
                  value={form.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  className="rounded-none border-slate-200 focus:border-brand-green focus:ring-brand-green resize-none"
                  data-testid="input-message"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand-orange text-white py-4 px-8 font-bold text-sm uppercase tracking-wider hover:bg-brand-orange-hover transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="submit-quote-button"
              >
                {loading ? t('quote.submitting') : t('quote.submit')}
                {!loading && <Send className="w-4 h-4" />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
